import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DeleteDialog from 'core/components/DeleteDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import CreateButton from 'core/components/buttons/CreateButton'
import ListContainer from 'core/containers/ListContainer'
import Text from 'core/elements/Text'
import PopoverButton, { PopoverButtonItem } from 'core/elements/button/PopoverButton'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick, prop } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { formatTimestampToLocal } from '../vms/helpers'
import CreateNetworkModal from './CreateNetworkModal'
import CreatePhysicalNetworkModal from './CreatePhysicalNetworkModal'
import EditPhysicalNetworkModal from './EditPhysicalNetworkModal'
import ShareNetworkWithSpecificTenants from './ShareNetworkWithSpecificTenants'
import { deleteNetwork, listNetworks } from './actions'
import { INetworkDetailsPageTabs } from './network-details/model'
import { networksSelector } from './selectors'

type ModelDataKey = DataKeys.OpenstackNetworks
type SelectorModel = ArrayElement<ReturnType<typeof networksSelector>>
type ActionParams = InferActionParams<typeof listNetworks>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackNetworks',
  listTablePrefs,
)

const searchTargets = [
  'name',
  'id',
  'subnetDetails',
  'provider:network_type',
  'status',
  'availableIps',
  'provider:physical_network',
  'provider:segmentation_id',
]

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const useNetworkModeBadgeStyles = makeStyles<Theme>((theme) => ({
  badge: {
    display: 'inline-block',
    borderRadius: '4px',
    border: `1px solid ${theme.palette.blue['700']}`,
    background: theme.palette.blue['100'],
    color: theme.palette.blue['700'],
    padding: '0 8px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '20px',
    marginLeft: '8px',
    whiteSpace: 'nowrap',
  },
  nameWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  nameLink: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '0 1 auto',
    minWidth: 0,
  },
}))

const NetworkModeBadge = ({ tags }: { tags?: string[] }) => {
  const classes = useNetworkModeBadgeStyles()
  const isSimpleNetwork = tags?.includes('simple_network')
  return (
    <Text variant="caption2" className={classes.badge}>
      {isSimpleNetwork ? 'Layer 2 Network' : 'Managed Network'}
    </Text>
  )
}

const NameWithBadgeCell = ({ value, item }) => {
  const classes = useNetworkModeBadgeStyles()
  const { history } = useReactRouter()

  const handleClick = () => {
    history.push(
      routeManager.routes.openstack.networkDetails.path({
        id: item.id,
        tab: INetworkDetailsPageTabs.Overview,
      }),
    )
  }

  return (
    <div className={classes.nameWithBadge}>
      <Text
        variant="body2"
        className={classes.nameLink}
        onClick={handleClick}
        style={{ cursor: 'pointer', color: '#0089C7' }}
      >
        {value}
      </Text>
      <NetworkModeBadge tags={item.tags} />
    </div>
  )
}

export const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value?.map((subnet) => (
        <Text variant="body2" key={subnet.id}>
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

export const SharedWithTenants = ({ value, item }) => {
  return (
    <>
      {value
        ? 'All Tenants'
        : item?.sharedTenants?.length > 0
        ? item?.sharedTenants?.map((i) => i.tenantName).join(', ')
        : 'None'}
    </>
  )
}

const networkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    render: (value, item) => <NameWithBadgeCell value={value} item={item} />,
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'type',
    label: 'Network',
    render: (val) => val.charAt(0).toUpperCase() + val.slice(1),
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'provider:network_type',
    label: 'Network Type',
  },
  {
    key: 'mtu',
    label: 'MTU',
  },

  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'provider:segmentation_id',
    label: 'Segmentation ID',
    render: (val) => (val ? val : 'N/A'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'dns_domain',
    label: 'DNS Zone',
    render: (val) => (val ? val : '-'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'availableIps',
    label: 'Available IPs',
  },
  {
    key: 'isSharedAcrossAllTenants',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

const ssuNetworkColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    render: (value, item) => <NameWithBadgeCell value={value} item={item} />,
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    render: (val) => (val ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'router:external',
    label: 'External',
    render: (val) => (val ? 'Yes' : 'No'),
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (val) => (val ? 'Up' : 'Down'),
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'shared',
    label: 'Shared Tenants',
    render: (value, item) => <SharedWithTenants item={item} value={value} />,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

/**
 * Networks List Page - Responsible for displaying and managing OpenStack networks
 * Handles network creation, sharing, and display logic
 * Provides UI for viewing networks, sharing with specific tenants, and creating new networks
 * For Admin users:
 * - When virtualNetworking is enabled
 * - Can view all networks
 * - Can share networks with specific tenants
 * - Can create both physical and virtual networks in their tenant
 * - When virtualNetworking is disabled,
 * - Can only view physical networks (no virtual networks)
 * - Cannot create virtual networks
 * For Self-service users:
 * - Can only view networks in their tenant and shared networks
 * - Can create virtual networks in their tenant
 * - Cannot share networks with other tenants
 *
 * VMware environments redirect to VMware networks page
 */
export default function NetworksListPage() {
  const [selectedNetwork, setSelectedNetwork] = useState<SelectorModel>(null)
  const [showShareNetworkModal, setShowShareNetworkModal] = useState<boolean>(false)
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history } = useReactRouter()
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features, virtualNetworking } = session
  const isAdminUser = isAdmin()
  const isReadonlyUserFlag = isReadonlyUser()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (isVmware) {
    history.push(routeManager.routes.openstack.vmwareNetworks.path())
  }

  // Fetch all networks
  const { message, loading, reload } = useListAction(listNetworks, {
    params,
  })
  const networks = useAppSelector(networksSelector)

  // Hide virtual networks if virtualNetworking is false
  // Note - virtualNetworking is currently enabled by default in Cluster Blueprint and cannot be disabled in any environment
  const hideVirtualNetworks = useMemo(() => {
    return !virtualNetworking
  }, [virtualNetworking])

  // Filter networks based on hideVirtualNetworks
  const networksInTenant = useMemo(() => {
    const belongsToCurrentTenant = (network: SelectorModel) =>
      network.project_id === currentTenant || network.shared

    const isPhysicalNetwork = (network: SelectorModel) =>
      network['provider:network_type'] === 'physical'

    return networks.filter((network) => {
      if (!belongsToCurrentTenant(network)) return false
      if (hideVirtualNetworks && !isPhysicalNetwork(network)) return false
      return true
    })
  }, [networks, currentTenant, hideVirtualNetworks])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Share Network With Specific Tenants',
      icon: 'edit',
      handleAction: ([network]) => {
        setSelectedNetwork(network)
        setShowShareNetworkModal(true)
      },
      refreshAfterSuccess: true,
      cond: (selectedRows) =>
        selectedRows.length === 1 &&
        selectedRows[0]?.project_id === currentTenant && // Only allow sharing networks owned by current tenant
        !selectedRows[0]?.isSharedAcrossAllTenants, // Only allow sharing networks that are not already shared with all tenants
      disabledTooltip: (selectedRows) =>
        selectedRows.length > 1
          ? 'You can only share network with specific tenants for a single network at a time'
          : selectedRows[0]?.isSharedAcrossAllTenants
          ? 'Network is already shared with all tenants'
          : selectedRows[0]?.project_id !== currentTenant
          ? 'You can only share networks owned by your tenant'
          : '',
    },
  ]

  const DeletePhysicalNetworkDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deleteNetwork}
        title="Delete Network(s)"
        resourceLabel="Networks"
      />
    ),
    [],
  )

  const items: PopoverButtonItem[] = useMemo(
    () => [
      {
        title: 'Physical Network',
        onClick: () => {
          history.push(routeManager.routes.openstack.createPhysicalNetwork.path())
        },
      },
      {
        title: 'Virtual Network',
        onClick: () => {
          history.push(routeManager.routes.openstack.createVirtualNetwork.path())
        },
      },
    ],
    [],
  )

  /**
   * Renders the appropriate create network button based on user permissions and virtual networking settings.
   * - Non-admin users or when virtual networks are hidden: Simple create button
   * - Admin users with virtual networks enabled: Popover button with options
   */
  const CreateNetworkButton = useMemo(() => {
    if (hideVirtualNetworks || !isAdminUser) {
      const handleClick = () => {
        const route = isAdminUser
          ? routeManager.routes.openstack.createPhysicalNetwork
          : routeManager.routes.openstack.createNetwork
        history.push(route.path())
      }

      return (
        <CreateButton data-testid="create-network-button-non-admin" onClick={handleClick}>
          Create Network
        </CreateButton>
      )
    }

    return <PopoverButton buttonLabel="Create Network" items={items} />
  }, [hideVirtualNetworks, isAdminUser, history, items])

  /**
   * Renders the appropriate network creation modals based on user permissions and virtual networking settings.
   * - Non-admin users: Only network creation modal
   * - Admin users: Physical network modal + conditional virtual network modal
   */
  const NetworkModals = useMemo(() => {
    if (!isAdminUser) {
      return <CreateNetworkModal addRoute={routeManager.routes.openstack.createNetwork} />
    }

    return (
      <>
        <CreatePhysicalNetworkModal
          addRoute={routeManager.routes.openstack.createPhysicalNetwork}
        />
        {!hideVirtualNetworks && (
          <CreateNetworkModal addRoute={routeManager.routes.openstack.createVirtualNetwork} />
        )}
        {showShareNetworkModal && (
          <ShareNetworkWithSpecificTenants
            onClose={() => {
              setSelectedNetwork(null)
              setShowShareNetworkModal(false)
            }}
            network={selectedNetwork}
          />
        )}
      </>
    )
  }, [isAdminUser, hideVirtualNetworks, showShareNetworkModal, selectedNetwork])

  return (
    <>
      <DocumentMeta title="Networks" />
      {!isReadonlyUserFlag && (
        <>
          <HeaderPrimaryActionPortal>{CreateNetworkButton}</HeaderPrimaryActionPortal>
          {NetworkModals}
        </>
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OpenstackNetworks}
        label="Networks"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={networksInTenant}
        columns={isAdminUser ? networkColumns : ssuNetworkColumns}
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteNetwork}
        DeleteDialogComponent={DeletePhysicalNetworkDialog}
        EditDialogComponent={EditPhysicalNetworkModal}
        batchActions={isAdminUser ? batchActions : []}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
